import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { CASE_STUDIES } from '../caseStudies';
import { SERVICE_PAGES } from '../servicePages';

const Heading = ({ label, title, children }) => (
  <div className="mb-12 max-w-3xl">
    <p className="section-label mb-4">{label}</p>
    <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-[1.05]">{title}</h2>
    {children}
  </div>
);

export const ServiceHero = ({ s, Icon }) => {
  const [before] = s.headline.split(s.yellow);
  return (
    <section className="relative pt-36 pb-20 lg:pt-44 lg:pb-24 overflow-hidden">
      <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-[#F5CD1B]/6 blur-[140px] -z-10"></div>
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className={s.heroWide ? 'lg:col-span-6' : 'lg:col-span-7'}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 shrink-0 rounded-lg bg-[#F5CD1B]/10 border border-[#F5CD1B]/25 flex items-center justify-center">
              <Icon size={20} className="text-[#F5CD1B]" />
            </div>
            <p className="section-label" data-testid="service-eyebrow">{s.name} · Liverpool · UK Wide</p>
          </div>
          <h1 data-testid="service-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-white">
            {before}<span className="text-[#F5CD1B]">{s.yellow}</span>
          </h1>
          {s.intro.map((p, i) => (
            <p key={i} className="text-white/70 text-base md:text-lg leading-relaxed mt-6 max-w-2xl">{p}</p>
          ))}
          <div className="flex flex-wrap gap-3 mt-10">
            <Link to="/contact" data-testid="service-quote-btn" className="btn-yellow inline-flex items-center gap-2 px-6 py-4 rounded-md uppercase text-sm">
              Request a proposal <ArrowRight size={16} />
            </Link>
            <Link to="/case-studies" data-testid="service-work-btn" className="btn-ghost inline-flex items-center gap-2 px-6 py-4 rounded-md uppercase text-sm">
              See our work
            </Link>
          </div>
        </div>
        <div className={s.heroWide ? 'lg:col-span-6' : 'lg:col-span-5'}>
          <div className={`rounded-2xl overflow-hidden border border-white/10 ${s.heroAspect || 'aspect-[4/5]'}`}>
            <img src={s.hero} alt={`${s.name} by Yellow, marketing agency Liverpool`} className="w-full h-full object-cover" style={{ objectPosition: s.heroPosition || 'center' }} />
          </div>
        </div>
      </div>
    </section>
  );
};

export const ServiceIncluded = ({ s }) => (
  <section className="py-24 lg:py-28">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
      <Heading label="What's included" title={`Our ${s.name.toLowerCase()} services.`} />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {s.included.map((item, i) => (
          <div key={i} data-testid={`service-included-${i}`} className="bg-[#111114] border border-white/10 rounded-2xl p-7 hover:border-[#F5CD1B]/30 transition-colors">
            <div className="w-8 h-8 rounded-md bg-[#F5CD1B] flex items-center justify-center">
              <Check size={16} className="text-[#0b0b0d]" strokeWidth={3} />
            </div>
            <h3 className="font-display text-xl font-bold text-white mt-5">{item.title}</h3>
            <p className="text-white/60 text-sm mt-3 leading-relaxed">{item.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const ServiceGallery = ({ s }) => (
  <section className="py-16 lg:py-20" data-testid="service-gallery">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
      <Heading label="Examples" title="A taste of our work." />
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:mb-4">
        {s.gallery.map((img) => (
          <figure key={img.src} className="break-inside-avoid rounded-xl overflow-hidden border border-white/10 bg-[#111114]">
            <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-auto hover:scale-[1.03] transition-transform duration-700" />
          </figure>
        ))}
      </div>
    </div>
  </section>
);

export const ServiceApproach = ({ s }) => (
  <section className="py-24 lg:py-28 bg-[#08080a]">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
      <Heading label="How it works" title="Four steps from brief to results." />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {s.approach.map((step, i) => (
          <div key={step.title} className="bg-[#111114] border border-white/10 rounded-2xl p-8">
            <div className="text-[#F5CD1B] font-display text-5xl font-bold">0{i + 1}</div>
            <h3 className="font-display text-2xl font-bold text-white mt-4">{step.title}</h3>
            <p className="text-white/60 text-sm mt-3 leading-relaxed">{step.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const ServiceWhy = ({ s, stats }) => (
  <section className="py-24">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <p className="section-label mb-4">Why Yellow</p>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-[1.05]">Why choose Yellow for <span className="text-[#F5CD1B]">{s.name.toLowerCase()}?</span></h2>
      </div>
      <div className="lg:col-span-7 space-y-5 text-white/75 text-base md:text-lg leading-relaxed">
        {s.why.map((p, i) => <p key={i}>{p}</p>)}
        <div className="grid grid-cols-2 gap-3 pt-4">
          {stats.map((st) => (
            <div key={st.label} className="bg-[#111114] border border-white/10 rounded-xl p-5">
              <div className="font-display font-bold text-[#F5CD1B] text-lg">{st.value}</div>
              <p className="text-white/60 text-xs mt-1">{st.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const ServiceCaseStudies = ({ s }) => {
  const items = s.caseStudies.map((slug) => CASE_STUDIES.find((c) => c.slug === slug)).filter(Boolean);
  return (
    <section className="py-24 bg-[#08080a]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <Heading label="Case studies" title="Real projects, real results." />
        <div className="grid md:grid-cols-3 gap-4">
          {items.map((c) => (
            <Link key={c.slug} to={`/case-studies/${c.slug}`} data-testid={`service-case-${c.slug}`} className="card-hover group rounded-2xl overflow-hidden border border-white/10 bg-[#111114]">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={c.hero} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <p className="text-[10px] uppercase tracking-widest text-[#F5CD1B]">{c.categoryLabel}</p>
                <h3 className="font-display text-lg font-bold text-white mt-2 leading-snug">{c.title}</h3>
                <p className="text-white/50 text-sm mt-2">{c.client}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ServiceFAQ = ({ s, compact = false }) => (
  <section className={compact ? 'py-14 lg:py-16' : 'py-24'}>
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
      <Heading label="Common questions" title={`${s.name} FAQs.`} />
      <Accordion type="single" collapsible className="space-y-3">
        {s.faqs.map((f, i) => (
          <AccordionItem key={i} value={`item-${i}`} data-testid={`service-faq-${i}`} className="bg-[#111114] border border-white/10 rounded-xl px-6 data-[state=open]:border-[#F5CD1B]/30">
            <AccordionTrigger className="text-left font-display text-lg text-white hover:text-[#F5CD1B] hover:no-underline">{f.q}</AccordionTrigger>
            <AccordionContent className="text-white/70 leading-relaxed pb-5">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export const ServiceRelated = ({ s }) => (
  <section className="pb-24">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
      <p className="section-label mb-6">Related services</p>
      <div className="flex flex-wrap gap-3">
        {s.related.map((slug) => (
          <Link key={slug} to={`/${slug}`} data-testid={`service-related-${slug}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 text-white/80 text-sm hover:border-[#F5CD1B] hover:text-[#F5CD1B] transition-colors">
            {SERVICE_PAGES[slug].label} <ArrowUpRight size={14} />
          </Link>
        ))}
      </div>
    </div>
  </section>
);
